import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Box,
  Button,
  Flex,
  Grid,
  Label,
  FileUpload,
  ErrorMsg,
  Toastify,
} from "atoms";
import { FormInput } from "molecules";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";
import { PLOT } from "utils/constants";

export const MasterPlanForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingMarketPlace,
      createPropertyListingMarketPlace,
      updatePropertyListingMarketPlace,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingMarketPlace(id)?.then((data) => {
        setProperty(data);

        // reformat images
        data &&
          setProperty((prev) => ({
            ...prev,
            sanctionPlanDocuments: data?.masterPlanImages?.find(
              (i) => i?.type === "sanctionPlanDocuments"
            )?.images,
            uploadMasterPlan: data?.masterPlanImages?.find(
              (i) => i?.type === "uploadMasterPlan"
            )?.images,
          }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({
      dirty,
      openArea,
      projectNumberOfUnits,
      // projectLandArea,
      densityOfUnits,
      achievedFAR,
      floorToCeilingHeight,
      parkArea,
      sanctionPlanDocuments,
      uploadMasterPlan,
    }) => {
      setSubmitLoading(true);
      // new nearbyAreas total

      if (dirty) {
        const formattedValues = {
          propertyId: id,
          openArea,
          projectNumberOfUnits,
          // projectLandArea,
          densityOfUnits,
          achievedFAR,
          floorToCeilingHeight,
          parkArea,
          masterPlanImages: [
            {
              type: "sanctionPlanDocuments",
              images: sanctionPlanDocuments,
            },
            {
              type: "uploadMasterPlan",
              images: uploadMasterPlan,
            },
          ],
        };
        if (property) {
          updatePropertyListingMarketPlace(
            formattedValues,
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Master plan updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Master plan not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingMarketPlace(formattedValues, rootProperty?.name)
            .then(() => {
              Toastify("success", "Master plan created");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Master plan not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingMarketPlace,
      id,
      updatePropertyListingMarketPlace,
      onNext,
      property,
      rootProperty,
      fetchProperty,
    ]
  );

  const validationSchema = Yup.object().shape({
    openArea: Yup.number()
      .min(0, "Value should be greater than 0")
      .typeError("Invalid number")
      .required("Open area is required"),
    projectNumberOfUnits: Yup.number()
      .min(0, "Value should be greater than 0")
      .typeError("Invalid number")
      .required("Project number of units is required"),
    // projectLandArea: Yup.number()
    //   .typeError("Project land area is required")
    //   .required("Project land area is required"),
    densityOfUnits: Yup.number()
      .min(0, "Value should be greater than 0")
      .typeError("Invalid number")
      .required("Density of units is required"),
    achievedFAR: Yup.number()
      .min(0, "Value should be greater than 0")
      .typeError("Invalid number")
      .required("Achieved far is required"),

    parkArea: Yup.number()
      .min(0, "Value should be greater than 0")
      .typeError("Invalid number")
      .required("Park area is required"),
    sanctionPlanDocuments: Yup.array()
      .min(1, "Add minimum 1 file")
      .typeError("Sanctioned plan is required")
      .required("Sanctioned plan is required"),
    uploadMasterPlan: Yup.array()
      .min(1, "Add minimum 1 image")
      .typeError("Master plan is required")
      .required("Master plan is required"),
    ...(!rootProperty?.types?.[0]?.name?.toLowerCase()?.includes(PLOT) && {
      floorToCeilingHeight: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Floor to ceiling height is required"),
    }),
  });

  return (
    <Formik
      initialValues={{
        openArea: "",
        projectNumberOfUnits: "",
        // projectLandArea: "",
        densityOfUnits: "",
        achievedFAR: "",
        floorToCeilingHeight: "",
        parkArea: "",
        sanctionPlanDocuments: [],
        uploadMasterPlan: [],
        ...property,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, dirty, submitForm, touched, errors }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });
        return (
          <Form>
            <Grid gridGap="2.4rem" mt="2.4rem">
              <Grid
                gridTemplateColumns="1fr 1fr"
                gridColumnGap="2.4rem"
                gridRowGap="1.6rem"
              >
                <Grid gridRowGap="1.6rem">
                  <Label text="Open Area (%)" req />
                  <FormInput
                    name="openArea"
                    // type="number"
                    placeholder="Enter Percentage"
                    req
                  />
                </Grid>
                <Grid gridRowGap="1.6rem">
                  <Label text="Project no. of Units" req />
                  <FormInput
                    name="projectNumberOfUnits"
                    // type="number"
                    placeholder="Enter Number"
                    req
                  />
                </Grid>
                {/* <Grid gridRowGap="1.6rem">
                  <Label text="Project Land Area" req />
                  <FormInput
                    name="projectLandArea"
                    type="number"
                    placeholder="Enter Number"
                    req
                  />
                </Grid> */}
                <Grid gridRowGap="1.6rem">
                  <Label text="Density of Units" req />
                  <FormInput
                    name="densityOfUnits"
                    // type="number"
                    placeholder="Enter Number"
                    req
                  />
                </Grid>
                <Grid gridRowGap="1.6rem">
                  <Label text="Achived FAR" req />
                  <FormInput
                    name="achievedFAR"
                    // type="number"
                    placeholder="Enter Number"
                    req
                  />
                </Grid>
                <Grid gridRowGap="1.6rem">
                  <Label text="Floor to Ceiling Height" req />
                  <FormInput
                    name="floorToCeilingHeight"
                    // type="number"
                    placeholder="Enter Number"
                    disabled={rootProperty?.types?.[0]?.name
                      ?.toLowerCase()
                      ?.includes(PLOT)}
                    req
                  />
                </Grid>
              </Grid>
              <Box>
                <Label text="Sanctioned Plan Documents" mb="1.6rem" req />
                {/* sanctionPlanDocuments */}
                <FileUpload
                  files={values?.sanctionPlanDocuments || []}
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.sanctionPlanDocuments
                        ? JSON.parse(
                            JSON.stringify(values?.sanctionPlanDocuments)
                          ) || []
                        : []),
                      ...locations?.map((i) => ({ image: i, alt: "" })),
                    ];

                    setFieldValue("sanctionPlanDocuments", newProjectImages);
                  }}
                  onSubmitDescription={({ alt, file }) => {
                    setFieldValue(
                      `sanctionPlanDocuments.[${file?.index}].alt`,
                      alt
                    );
                  }}
                  onCross={(index) => {
                    const currProjectImages = [
                      ...(values?.sanctionPlanDocuments
                        ? JSON.parse(
                            JSON.stringify(values?.sanctionPlanDocuments)
                          ) || []
                        : []),
                    ];
                    const total = currProjectImages?.length;

                    const newProjectImages = [
                      ...currProjectImages?.slice(0, index),
                      ...currProjectImages?.slice(index + 1, total),
                    ];

                    setFieldValue("sanctionPlanDocuments", newProjectImages);
                  }}
                  inputFormat="application/pdf"
                  ext={["pdf"]}
                />
                {touched?.sanctionPlanDocuments &&
                  errors?.sanctionPlanDocuments && (
                    <ErrorMsg text={errors?.sanctionPlanDocuments} />
                  )}
                {/* <Flex aI="center" mt="1rem">
                  <Checkbox
                    name="noData"
                    height="1.8rem"
                    checked={values.noData}
                  />
                  <Text color="neutral.700">No Data</Text>
                </Flex> */}
              </Box>
              <Box>
                <Label text="Upload Master Plan" mb="1.6rem" req />
                {/* uploadMasterPlan */}
                <FileUpload
                  files={values?.uploadMasterPlan || []}
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.uploadMasterPlan
                        ? JSON.parse(
                            JSON.stringify(values?.uploadMasterPlan)
                          ) || []
                        : []),
                      ...locations?.map((i) => ({ image: i, alt: "" })),
                    ];

                    setFieldValue("uploadMasterPlan", newProjectImages);
                  }}
                  onSubmitDescription={({ alt, file }) => {
                    setFieldValue(`uploadMasterPlan.[${file?.index}].alt`, alt);
                  }}
                  onCross={(index) => {
                    const currProjectImages = [
                      ...(values?.uploadMasterPlan
                        ? JSON.parse(
                            JSON.stringify(values?.uploadMasterPlan)
                          ) || []
                        : []),
                    ];
                    const total = currProjectImages?.length;

                    const newProjectImages = [
                      ...currProjectImages?.slice(0, index),
                      ...currProjectImages?.slice(index + 1, total),
                    ];

                    setFieldValue("uploadMasterPlan", newProjectImages);
                  }}
                />
                {touched?.uploadMasterPlan && errors?.uploadMasterPlan && (
                  <ErrorMsg text={errors?.uploadMasterPlan} />
                )}
                {/* <Flex aI="center" mt="1rem">
                  <Checkbox
                    name="noData"
                    height="1.8rem"
                    checked={values.noData}
                  />
                  <Text color="neutral.700">No Data</Text>
                </Flex> */}
              </Box>
              <Grid
                gridTemplateColumns="1fr 1fr"
                gridColumnGap="2.4rem"
                gridRowGap="1.6rem"
              >
                <Grid gridRowGap="1.6rem">
                  <Label text="Park Area" req />
                  <FormInput
                    name="parkArea"
                    // type="number"
                    placeholder="Enter Acres"
                    req
                  />
                </Grid>
              </Grid>

              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
